/**
 * Inspection Template Editor
 * 
 * Manager-only page for viewing, editing, and publishing inspection templates.
 * Features:
 * - Clone-on-edit versioning (published templates are immutable)
 * - 3-panel structural editor (systems → items → item properties)
 * - Drag-and-drop reordering
 * - Version history with change notes
 */

import { useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { TemplateList } from '@/components/templates/TemplateList';
import { TemplateEditor } from '@/components/templates/TemplateEditor';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import { ClipboardList, ArrowLeft } from 'lucide-react';

export default function InspectionTemplates() {
  const { templateId } = useParams<{ templateId?: string }>();
  const { activeShop } = useAuth();
  const permissions = usePermissions();
  const navigate = useNavigate();
  
  // Addon gate removed (Phase 2: templates accessible without OPTIDVI addon)
  
  // Permission gate - Manager/ORG_ADMIN only (already handled by route, but belt-and-suspenders)
  if (!permissions.hasRole('MANAGER', 'ORG_ADMIN')) {
    return <Navigate to="/" replace />;
  }

  const shopId = activeShop?.id;
  
  if (!shopId) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">No shop selected</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <TopBar />
      <div className="flex-1 container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ClipboardList className="h-6 w-6 text-primary" />
              Inspection Templates
            </h1>
            <p className="text-muted-foreground">
              Manage inspection checklists for your shop
            </p>
          </div>
          <Button onClick={() => navigate('/shop-profile?tab=admin')} variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        {templateId ? (
          <TemplateEditor templateId={templateId} shopId={shopId} />
        ) : (
          <TemplateList shopId={shopId} />
        )}
      </div>
    </div>
  );
}
